<template>
  <form @submit.prevent :novalidate="true">
    <div class="col-1 ms-auto" v-bind:class="{ inlineSpinner: isBusy }"></div>

    <b-card>
      <template v-slot:header>
        <div class="mb-3 row mb-0">
          <div class="col-lg-6">
            <input class="form-control" type="text" readonly
              :value="generalSettings ? `${generalSettings.Database} (v${generalSettings.DatabaseVersion})` : ''" />
          </div>
        </div>
      </template>

      <BAccordion free
                  v-if="generalSettings"
                  class="d-flex flex-column">
        <BAccordionItem class="mb-3" show>
          <template #title>
            <h5 class="mb-0">
              {{ $t('Webportal.Administration.GeneralSettings') }}
            </h5>
          </template>

          <!-- Environment -->
          <div class="mb-3 row">
            <label for="Environment" class="col-lg-4 col-md-6 col-form-label pt-0">Environment</label>
            <div class="col">
              <b-form-radio-group id="Environment"
                                  buttons
                                  button-variant="outline-secondary"
                                  size="sm"
                                  v-model="generalSettings.Environment"
                                  @change="v$.generalSettings.Environment.$touch()"
                                  :disabled="isBusy">
                <b-form-radio :value="'Development'">Development</b-form-radio>
                <b-form-radio :value="'Staging'">Staging</b-form-radio>
                <b-form-radio :value="'Production'">Production</b-form-radio>
                <b-form-radio :value="'Demo'">Demo</b-form-radio>
              </b-form-radio-group>
            </div>
          </div>

          <!-- LoopVersion -->
          <div class="mb-3 row">
            <label for="LoopVersion" class="col-lg-4 col-md-6 col-form-label pt-0">LoopVersion</label>
            <div class="col">
              <b-form-radio-group id="LoopVersion"
                                  buttons
                                  button-variant="outline-secondary"
                                  size="sm"
                                  v-model="generalSettings.LoopVersion"
                                  @change="v$.generalSettings.LoopVersion.$touch()"
                                  :disabled="isBusy">
                <b-form-radio :value="'v1'">v1</b-form-radio>
                <b-form-radio :value="'v2'">v2</b-form-radio>
              </b-form-radio-group>
            </div>
          </div>

          <!-- WebportalUrl -->
          <div class="mb-3 row">
            <label for="WebportalUrl" class="col-lg-4 col-md-6 col-form-label pt-0">WebportalUrl</label>
            <div class="col">
              <b-form-input id="WebportalUrl"
                            v-model="generalSettings.WebportalUrl"
                            @change="v$.generalSettings.WebportalUrl.$touch()"
                            size="sm"
                            :disabled="isBusy">
              </b-form-input>
            </div>
            <div class="col-auto">
              <b-button variant="secondary" size="sm" @click="setWebportalUrl()">
                <font-awesome-icon :icon="['fal', 'window']" /> {{ $t('Webportal.Apply') }}
              </b-button>
            </div>
          </div>

          <!-- PlatformCode -->
          <div class="mb-3 row">
            <label for="PlatformCode" class="col-lg-4 col-md-6 col-form-label pt-0">PlatformCode</label>
            <div class="col-lg-4 col-md-6">
              <b-form-select  id="PlatformCode"
                              v-model="generalSettings.PlatformCode"
                              @change="v$.generalSettings.PlatformCode.$touch()"
                              size="sm"
                              :disabled="isBusy">
                <b-form-select-option v-for="platform in generalSettings.Platforms"
                                      :key="platform.Code"
                                      :value="platform.Code">
                  {{ platform.Code }} - {{ platform.Name }}
                </b-form-select-option>
              </b-form-select>
            </div>
          </div>

          <hr />

          <!-- CreateDefaultNotificationForExternalUsers -->
          <div class="mb-3 row">
            <label for="CreateDefaultNotificationForExternalUsers" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.CreateDefaultNotificationForExternalUsers') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay"
                :title="$t('Webportal.Administration.CreateDefaultNotificationForExternalUsers.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input"
                        type="checkbox"
                        v-model="generalSettings.CreateDefaultNotificationForExternalUsers"
                        @change="v$.generalSettings.CreateDefaultNotificationForExternalUsers.$touch()"
                        :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>
        </BAccordionItem>

        <BAccordionItem class="mb-3" show>
          <template #title>
            <h5 class="mb-0">
              {{ $t('Webportal.Administration.SupportInformation') }}
            </h5>
          </template>
          <support-information  :value="generalSettings.SupportInformation"
                                ref="supportInformation"
                                @validation="onSupportValidation"
                                :disabled="generalSettings.SupportInformation == null" />
        </BAccordionItem>
      </BAccordion>

      <template v-slot:footer>
        <div class="float-end">
          <button type="button" @click="cancel" class="btn btn-secondary" :disabled="!isFormChanged || isBusy">
            <font-awesome-icon :icon="['far', 'ban']" /> {{ $t('Webportal.Cancel') }}
          </button>
          <button type="button" @click="submit" class="btn btn-primary"
            :disabled="!isFormChanged || !isFormValid || isBusy">
            <font-awesome-icon :icon="['far', 'dot-circle']" /> {{ $t('Webportal.Save') }}
          </button>
        </div>
      </template>
    </b-card>
  </form>
</template>

<script>
import AppConfig from '@/mixins/AppConfig-APP_TARGET';
import AdministrationService from '@/services/AdministrationService';
import CollapsiblePanel from '@/components/CollapsiblePanel';
import SupportInformation from './SupportInformation.vue';

import useVuelidate from '@vuelidate/core';
import { required, email, helpers } from '@vuelidate/validators';

export default {
  name: "GeneralSettings",

  mixins: [AppConfig],

  components: { CollapsiblePanel, SupportInformation },

  setup () {
    return { v$: useVuelidate() }
  },

  validations() {
    return {
      generalSettings: {
        WebportalUrl: { required },
        Environment: { required },
        LoopVersion: { required },
        PlatformCode: { required },
        CreateDefaultNotificationForExternalUsers: {},
        SupportInformation: { /* ... */ }
      }
    }
  },

  data() {
    return {
      generalSettings: {},

      isBusy: true,
      supportValidation: { valid: true, dirty: false }
    }
  },

  mounted() {
    this.initializeTheme();
  },

  computed: {
    isFormChanged() {
      const supportVuelidate = this.$refs.supportInformation?.v$;
      const isSupportDirty = supportVuelidate ? supportVuelidate.$anyDirty : false;

      return this.v$.$anyDirty || isSupportDirty;
    },

    isFormValid() {
      const supportVuelidate = this.$refs.supportInformation?.v$;
      const isSupportValid = supportVuelidate ? !supportVuelidate.$error : true;

      return !this.v$.$error && this.v$.$anyDirty && isSupportValid;
    },
  },

  activated() {
    this.getGeneralSettings();
  },

  beforeRouteLeave(to, from, next) {
    this.$emit('unsavedChanges', this.isFormChanged, next);
  },

  methods: {
    getGeneralSettings() {
      this.isBusy = true;
      AdministrationService.getGeneralSettings()
        .then(response => {
          this.generalSettings = response.data ? response.data[0] : null;
          this.v$.$reset();
        })
        .finally(() => {
          this.isBusy = false;
        });
    },

    setWebportalUrl() {
      this.generalSettings.WebportalUrl = window.location.origin;
    },


    onSupportValidation(status) {
      this.supportValidation = status;
    },

    cancel() {
      this.$emit('unsavedChanges', this.isFormChanged, result => {
        if (result !== false) {
          this.getGeneralSettings();
        }
      });
    },

    submit() {
      this.isBusy = true;

      AdministrationService.setGeneralSettings(this.generalSettings)
        .then(response => {
          this.isBusy = false;
          this.v$.$reset();
        })
        .catch(_ => { this.isBusy = false; });
    }
  }
}
</script>
